/**
 * Lazy-loaded Sentry wrapper.
 *
 * Why: synchronously importing `@sentry/react` in main.tsx pulls a ~107 kB
 * gzipped vendor chunk into the critical path (mostly from the replay
 * integration). For a journal app where LCP matters, that's too expensive.
 *
 * Strategy:
 *   - main.tsx schedules `initSentryDeferred()` for the next idle callback.
 *   - Until Sentry actually loads, calls to `captureException` and
 *     `setUser` are buffered into an in-memory queue.
 *   - Once `Sentry.init` completes, the queue is drained.
 *
 * Trade-off: errors thrown in the ~50-200 ms window before init are still
 * captured, but their stack traces may show post-load context. Acceptable
 * for the LCP win.
 */

// Lazy-imported namespace. `null` until init completes.
type SentryNS = typeof import("@sentry/react");
let SentryNS: SentryNS | null = null;

interface QueuedError {
  error: unknown;
  context?: Record<string, unknown>;
}

const queue: { errors: QueuedError[]; user: { id: string } | null | undefined } = {
  errors: [],
  // `undefined` means "no user update queued"; `null` means "set null on init".
  user: undefined,
};

export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (SentryNS) {
    SentryNS.captureException(error, context ? { extra: context } : undefined);
    return;
  }
  // Cap the queue so a runaway error loop pre-init can't OOM the page.
  if (queue.errors.length < 50) {
    queue.errors.push({ error, context });
  }
}

export function setUser(user: { id: string } | null) {
  if (SentryNS) {
    SentryNS.setUser(user);
    return;
  }
  queue.user = user;
}

export async function initSentryDeferred() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info("[sentry] VITE_SENTRY_DSN not set — Sentry disabled");
    }
    return;
  }

  // The dynamic import is what defers the vendor-sentry chunk.
  const Sentry = await import("@sentry/react");

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || undefined,

    sendDefaultPii: false,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        networkDetailAllowUrls: [],
        blockAllMedia: false,
      }),
    ],

    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    tracePropagationTargets: [
      /^https:\/\/.*\.supabase\.co\//,
      /^https:\/\/.*\.functions\.supabase\.co\//,
      "localhost",
    ],

    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint.originalException;
      const message =
        typeof error === "object" && error && "message" in error
          ? String((error as { message: unknown }).message)
          : "";

      if (
        message.includes("ResizeObserver loop") ||
        message.includes("Non-Error promise rejection captured") ||
        message.includes("Network request failed")
      ) {
        return null;
      }

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console" && breadcrumb.level === "debug") {
        return null;
      }
      return breadcrumb;
    },
  });

  SentryNS = Sentry;

  // Drain the pre-init queue. Errors first, then user, so any error
  // captured before the user set still gets the right user context once
  // setUser fires (Sentry tags subsequent events; older events keep
  // the user that was active when they were buffered, which is acceptable).
  for (const e of queue.errors) {
    Sentry.captureException(e.error, e.context ? { extra: e.context } : undefined);
  }
  queue.errors.length = 0;

  if (queue.user !== undefined) {
    Sentry.setUser(queue.user);
    queue.user = undefined;
  }
}
